import { getApiClient } from './api-client';
import { Workspace } from '../types/schema';

export interface CreateWorkspaceRequest {
  name: string;
  slug: string;
  description?: string;
}

export interface UpdateWorkspaceRequest {
  name?: string;
  slug?: string;
  description?: string;
}

// API response type (camelCase from backend)
interface WorkspaceApiResponse {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

// Map API response to frontend Workspace type
const mapWorkspaceResponse = (response: WorkspaceApiResponse): Workspace => ({
  id: response.id,
  name: response.name,
  slug: response.slug,
  description: response.description || undefined,
  created_at: new Date(response.createdAt),
  updated_at: new Date(response.updatedAt),
  deleted_at: response.deletedAt ? new Date(response.deletedAt) : undefined,
});

class WorkspaceService {
  async createWorkspace(data: CreateWorkspaceRequest): Promise<Workspace> {
    const client = getApiClient();
    const response = await client.post<WorkspaceApiResponse>('/workspaces', data);
    return mapWorkspaceResponse(response);
  }

  async getWorkspaces(): Promise<Workspace[]> {
    const client = getApiClient();
    const response = await client.get<WorkspaceApiResponse[]>('/workspaces');
    return response.map(mapWorkspaceResponse);
  }

  async getWorkspace(id: number): Promise<Workspace> {
    const client = getApiClient();
    const response = await client.get<WorkspaceApiResponse>(`/workspaces/${id}`);
    return mapWorkspaceResponse(response);
  }

  async getWorkspaceBySlug(slug: string): Promise<Workspace> {
    const client = getApiClient();
    const response = await client.get<WorkspaceApiResponse>(`/workspaces/slug/${slug}`);
    return mapWorkspaceResponse(response);
  }

  async updateWorkspace(
    id: number,
    data: UpdateWorkspaceRequest
  ): Promise<Workspace> {
    const client = getApiClient();
    const response = await client.patch<WorkspaceApiResponse>(`/workspaces/${id}`, data);
    return mapWorkspaceResponse(response);
  }

  async deleteWorkspace(id: number): Promise<void> {
    const client = getApiClient();
    return client.delete<void>(`/workspaces/${id}`);
  }

}

export const workspaceService = new WorkspaceService();
